import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    CircularProgress,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    TextField,
    TablePagination
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    Speed as SpeedIcon,
    Psychology as BrainIcon,
    LocalLibrary as BookIcon,
    Timer as TimerIcon,
    School as SchoolIcon,
    Assignment as AssignmentIcon,
    Star as StarIcon,
    Share as ShareIcon,
    LocalFireDepartment as FireIcon,
    Lightbulb as LightbulbIcon,
    Insights as InsightsIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    TrendingUp as TrendingUpIcon,
    Bolt as BoltIcon,
    Verified as VerifiedIcon,
    AccessTime as AccessTimeIcon,
    Flag as FlagIcon,
    Diversity3 as Diversity3Icon,
    MenuBook as MenuBookIcon,
    WorkspacePremium as PremiumIcon,
    People as PeopleIcon,
    Translate as TranslateIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import html2canvas from 'html2canvas';

const BADGE_CATEGORIES = {
    all: {
        title: 'Overall',
        icon: TrophyIcon,
        color: '#FFD700'
    },
    reading: {
        title: 'Reading',
        icon: BookIcon,
        color: '#4CAF50'
    },
    speed: {
        title: 'Peak Speed',
        icon: BoltIcon,
        color: '#F44336'
    },
    speedSustained: {
        title: 'Sustained Speed',
        icon: TrendingUpIcon,
        color: '#E91E63'
    },
    speedComprehension: {
        title: 'Speed + Brain',
        icon: SpeedIcon,
        color: '#FF5722'
    },
    speedVolume: {
        title: 'Fast Reads',
        icon: VerifiedIcon,
        color: '#D32F2F'
    },
    comprehension: {
        title: 'Comprehension',
        icon: BrainIcon,
        color: '#9C27B0'
    },
    dedication: {
        title: 'Dedication',
        icon: AccessTimeIcon,
        color: '#607D8B'
    },
    consistency: {
        title: 'Quizzes',
        icon: AssignmentIcon,
        color: '#795548'
    },
    streaks: {
        title: 'Streaks',
        icon: FireIcon,
        color: '#FF9800'
    },
    insights: {
        title: 'Expertise',
        icon: SchoolIcon,
        color: '#2196F3'
    },
    milestones: {
        title: 'Milestones',
        icon: FlagIcon,
        color: '#009688'
    },
    flex: {
        title: 'Flex',
        icon: PremiumIcon,
        color: '#FFD700'
    },
    affiliate: {
        title: 'Referrals',
        icon: PeopleIcon,
        color: '#00BCD4'
    },
    language: {
        title: 'Languages',
        icon: TranslateIcon,
        color: '#3F51B5'
    },
    vocabulary: {
        title: 'Vocabulary',
        icon: MenuBookIcon,
        color: '#8BC34A'
    }
};

// Spotlight badges with different icons and colors
const SPOTLIGHT_BADGES = {
    topReader: {
        title: 'Top Reader',
        description: 'Most words read overall',
        icon: BookIcon,
        color: '#4CAF50'
    },
    speedDemon: {
        title: 'Speed Demon',
        description: 'Highest average reading speed',
        icon: SpeedIcon,
        color: '#F44336'
    },
    comprehensionMaster: {
        title: 'Comprehension Master',
        description: 'Best quiz performance',
        icon: BrainIcon,
        color: '#9C27B0'
    },
    streakChampion: {
        title: 'Streak Champion',
        description: 'Longest current reading streak',
        icon: FireIcon,
        color: '#FF9800'
    },
    subjectExpert: {
        title: 'Subject Expert',
        description: 'Most subject expertise areas',
        icon: SchoolIcon,
        color: '#2196F3'
    }
};

// Component for reader card that can be shared
const ReaderCard = ({ user, onClose, onShare }) => {
    const theme = useTheme();
    const cardRef = React.useRef();
    const [sharing, setSharing] = useState(false);

    // Get user's spotlight badges
    const spotlightBadges = Object.entries(user.spotlight || {})
        .filter(([_, value]) => value === true)
        .map(([key]) => key);

    // Get user's earned badges
    const earnedBadges = Object.entries(user.badges || {})
        .flatMap(([category, badges]) => 
            Object.entries(badges)
                .filter(([_, badge]) => badge.earned)
                .map(([_, badge]) => ({
                    ...badge,
                    category
                }))
        );

    const handleShare = async () => {
        if (!cardRef.current) return;
        
        try {
            setSharing(true);
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: theme.palette.background.paper,
                scale: 2, // Higher quality
            });
            
            // Convert to blob
            canvas.toBlob(async (blob) => {
                try {
                    // Create a file from the blob
                    const file = new File([blob], "reader-flex-card.png", { type: 'image/png' });
                    
                    // Check if Web Share API is available
                    if (navigator.share) {
                        await navigator.share({
                            title: 'My ReaderFlex Stats',
                            text: 'Check out my reading achievements on ReaderFlex!',
                            files: [file]
                        });
                    } else {
                        // Fallback - create a download link
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'reader-flex-card.png';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                    onShare();
                } catch (error) {
                    console.error('Error sharing:', error);
                } finally {
                    setSharing(false);
                }
            }, 'image/png');
        } catch (error) {
            console.error('Error creating canvas:', error);
            setSharing(false);
        }
    };

    return (
        <>
            <DialogTitle>
                Reader Profile
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box ref={cardRef} sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar 
                            sx={{ 
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                width: 56,
                                height: 56,
                                mr: 2,
                                fontSize: '1.5rem'
                            }}
                        >
                            {user.displayName ? user.displayName.substring(0, 1).toUpperCase() : '?'}
                        </Avatar>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h5" fontWeight="bold">
                                    {user.displayName}
                                </Typography>
                                <Chip label={`Lvl ${user.level || 1}`} size="small"
                                    sx={{ backgroundColor: '#1976d220', color: '#1976d2', fontWeight: 600 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {user.title || 'Beginner Reader'} · {(user.xp || 0).toLocaleString()} XP · {user.totalBadges || 0} Badges
                            </Typography>
                        </Box>
                    </Box>

                    {/* Spotlight badges */}
                    {spotlightBadges.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Spotlight Achievements
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {spotlightBadges.map((badge) => {
                                    const SpotlightIcon = SPOTLIGHT_BADGES[badge].icon;
                                    return (
                                        <Chip
                                            key={badge}
                                            label={SPOTLIGHT_BADGES[badge].title}
                                            icon={<SpotlightIcon />}
                                            sx={{
                                                backgroundColor: SPOTLIGHT_BADGES[badge].color + '20',
                                                color: SPOTLIGHT_BADGES[badge].color,
                                                borderWidth: 1,
                                                borderStyle: 'solid',
                                                borderColor: SPOTLIGHT_BADGES[badge].color,
                                                fontWeight: 'bold',
                                                '& .MuiChip-icon': {
                                                    color: SPOTLIGHT_BADGES[badge].color,
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Statistics */}
                    <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.background.default }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Reading Stats
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Words Read:</Typography>
                                <Typography variant="body1" fontWeight="bold">{user.readingStats.totalWordsRead.toLocaleString()}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Reading Speed:</Typography>
                                <Typography variant="body1" fontWeight="bold">{user.readingStats.averageWPM} WPM</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Comprehension:</Typography>
                                <Typography variant="body1" fontWeight="bold">{Math.round(user.readingStats.comprehensionRate)}%</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Current Streak:</Typography>
                                <Typography variant="body1" fontWeight="bold">{user.readingStats.currentStreak} days</Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Top badges */}
                    {earnedBadges.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Top Achievements
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {earnedBadges.slice(0, 5).map((badge, index) => (
                                    <Tooltip key={index} title={badge.description || ''}>
                                        <Chip
                                            label={badge.title}
                                            size="small"
                                            icon={<span>{badge.icon}</span>}
                                            sx={{ 
                                                backgroundColor: BADGE_CATEGORIES[badge.category]?.color + '20',
                                                color: BADGE_CATEGORIES[badge.category]?.color
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleShare} 
                    startIcon={<ShareIcon />}
                    variant="contained"
                    disabled={sharing}
                >
                    {sharing ? 'Creating Share Image...' : 'Share Card'}
                </Button>
            </DialogActions>
        </>
    );
};

const LeaderboardCard = ({ user, rank, category, period, onShowProfile }) => {
    const theme = useTheme();
    const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32']; // Gold, Silver, Bronze
    
    // Get spotlight icons if user has any
    const spotlightIcons = Object.entries(user.spotlight || {})
        .filter(([_, value]) => value === true)
        .map(([key]) => {
            const SpotlightIcon = SPOTLIGHT_BADGES[key].icon;
            return (
                <Tooltip key={key} title={SPOTLIGHT_BADGES[key].title}>
                    <SpotlightIcon 
                        sx={{ 
                            color: SPOTLIGHT_BADGES[key].color,
                            marginLeft: 0.5 
                        }} 
                    />
                </Tooltip>
            );
        });
    
    return (
        <Card 
            sx={{ 
                mb: 2,
                transition: 'transform 0.2s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4]
                }
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                        sx={{ 
                            bgcolor: rank < 3 ? rankColors[rank] : theme.palette.grey[300],
                            color: rank < 3 ? 'black' : 'white',
                            mr: 2,
                            fontWeight: 'bold'
                        }}
                    >
                        {rank + 1}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h6">
                                {user.displayName}
                            </Typography>
                            <Chip label={`Lvl ${user.level || 1}`} size="small"
                                sx={{ backgroundColor: '#1976d220', color: '#1976d2', fontWeight: 600, height: 22, fontSize: '0.75rem' }} />
                            {spotlightIcons}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {user.title || 'Beginner Reader'} · {user.totalBadges || 0} Badges · {(user.xp || 0).toLocaleString()} XP
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        {period === 'skirmish' ? (
                            <>
                                <Typography variant="h6" sx={{ color: '#FF5722' }}>
                                    {(user.monthlyXP || 0).toLocaleString()} XP
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {user.monthlyArticlesRead || 0} articles this month
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" color="primary">
                                    {user.readingStats.averageWPM} WPM
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {Math.round(user.readingStats.comprehensionRate)}% Comprehension
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {category === 'all' && Object.entries(user.badges || {}).map(([badgeCategory, badges]) => 
                            Object.values(badges)
                                .filter(badge => badge.earned)
                                .slice(0, 3) // Limit to 3 badges per category for cleaner display
                                .map((badge, index) => (
                                    <Chip
                                        key={`${badgeCategory}-${index}`}
                                        label={badge.title}
                                        icon={<span>{badge.icon}</span>}
                                        size="small"
                                        sx={{ 
                                            backgroundColor: BADGE_CATEGORIES[badgeCategory]?.color + '20',
                                            color: BADGE_CATEGORIES[badgeCategory]?.color
                                        }}
                                    />
                                ))
                        )}
                        {category !== 'all' && Object.values(user.badges[category] || {})
                            .filter(badge => badge.earned)
                            .map((badge, index) => (
                                <Chip
                                    key={index}
                                    label={badge.title}
                                    icon={<span>{badge.icon}</span>}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: BADGE_CATEGORIES[category].color + '20',
                                        color: BADGE_CATEGORIES[category].color
                                    }}
                                />
                            ))
                        }
                    </Box>
                    <Button 
                        variant="outlined" 
                        size="small" 
                        startIcon={<ShareIcon />}
                        onClick={() => onShowProfile(user)}
                    >
                        Profile
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

// Spotlight section to highlight top performers
const ReaderSpotlight = ({ spotlightUsers, onShowProfile }) => {
    if (!spotlightUsers || spotlightUsers.length === 0) return null;
    
    return (
        <Paper sx={{ mb: 4, p: 3, overflow: 'hidden' }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LightbulbIcon sx={{ mr: 1, color: '#FFC107' }} />
                Reader Spotlight
            </Typography>
            <Typography color="text.secondary" paragraph>
                Congratulations to our top performers in different reading categories!
            </Typography>
            
            <Grid container spacing={2}>
                {Object.entries(SPOTLIGHT_BADGES).map(([key, badge]) => {
                    const user = spotlightUsers.find(u => u.spotlight && u.spotlight[key]);
                    if (!user) return null;
                    
                    const BadgeIcon = badge.icon;
                    return (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    borderLeft: `4px solid ${badge.color}`,
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Avatar 
                                            sx={{ 
                                                bgcolor: badge.color + '20',
                                                color: badge.color,
                                                mr: 1 
                                            }}
                                        >
                                            <BadgeIcon />
                                        </Avatar>
                                        <Typography variant="h6" color={badge.color}>
                                            {badge.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {badge.description}
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="subtitle1">
                                                {user.displayName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {key === 'topReader' && `${user.readingStats.totalWordsRead.toLocaleString()} words`}
                                                {key === 'speedDemon' && `${user.readingStats.averageWPM} WPM`}
                                                {key === 'comprehensionMaster' && `${Math.round(user.readingStats.comprehensionRate)}% avg.`}
                                                {key === 'streakChampion' && `${user.readingStats.currentStreak} day streak`}
                                                {key === 'subjectExpert' && `${user.readingStats.expertSubjects} subjects`}
                                            </Typography>
                                        </Box>
                                        <Button 
                                            size="small" 
                                            onClick={() => onShowProfile(user)}
                                            sx={{ color: badge.color }}
                                        >
                                            Profile
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
};

const Leaderboard = () => {
    const [selectedPeriod, setSelectedPeriod] = useState('legends');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [leaderboard, setLeaderboard] = useState([]);
    const [spotlightUsers, setSpotlightUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [shareAlert, setShareAlert] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOptions, setFilterOptions] = useState({
        showFriends: false,
        minWPM: 0,
        maxWPM: 10000
    });
    const { currentUser } = useAuth();

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                setLoading(true);
                
                // Map client categories to server API endpoints
                const categoryMapping = {
                    'all': '',
                    'reading': 'reading',
                    'speed': 'speed',
                    'speedSustained': 'speedSustained',
                    'speedComprehension': 'speedComprehension',
                    'speedVolume': 'speedVolume',
                    'comprehension': 'comprehension',
                    'dedication': 'dedication',
                    'consistency': 'consistency',
                    'streaks': 'streaks',
                    'insights': 'insights',
                    'milestones': 'milestones',
                    'flex': 'flex',
                    'affiliate': 'affiliate',
                    'language': 'language',
                    'vocabulary': 'vocabulary'
                };
                
                const serverCategory = categoryMapping[selectedCategory] || '';
                const endpoint = serverCategory 
                    ? `/users/badges/leaderboard/${serverCategory}` 
                    : '/users/badges/leaderboard';
                
                const response = await api.get(endpoint, { params: { period: selectedPeriod } });
                
                // Apply client-side sorting to ensure correct order
                const categoryBadgeCount = (user, cat) => {
                    const catBadges = user.badges?.[cat] || {};
                    return Object.values(catBadges).filter(b => b.earned).length;
                };

                const sortedData = [...response.data].sort((a, b) => {
                    switch(selectedCategory) {
                        case 'reading':
                            return b.readingStats.totalWordsRead - a.readingStats.totalWordsRead;
                        case 'speed':
                            return (b.readingStats.bestWPM || 0) - (a.readingStats.bestWPM || 0);
                        case 'speedSustained':
                            return b.readingStats.averageWPM - a.readingStats.averageWPM;
                        case 'speedComprehension':
                            return (b.readingStats.bestSpeedWithComprehension || 0) - (a.readingStats.bestSpeedWithComprehension || 0);
                        case 'speedVolume':
                            return (b.readingStats.fastReads?.above1000 || 0) - (a.readingStats.fastReads?.above1000 || 0);
                        case 'comprehension':
                            return b.readingStats.comprehensionRate - a.readingStats.comprehensionRate;
                        case 'dedication':
                            return (b.readingStats.totalReadingTime || 0) - (a.readingStats.totalReadingTime || 0);
                        case 'consistency':
                            return (b.readingStats.quizzesPassed || 0) - (a.readingStats.quizzesPassed || 0);
                        case 'streaks':
                            return b.readingStats.currentStreak - a.readingStats.currentStreak;
                        case 'insights':
                            return (b.readingStats.expertSubjects || 0) - (a.readingStats.expertSubjects || 0);
                        case 'milestones':
                            return (b.readingStats.articlesRead || 0) - (a.readingStats.articlesRead || 0);
                        case 'affiliate':
                            return (b.affiliateStats?.referrals || 0) - (a.affiliateStats?.referrals || 0);
                        case 'language':
                        case 'vocabulary':
                        case 'flex':
                            return categoryBadgeCount(b, selectedCategory) - categoryBadgeCount(a, selectedCategory);
                        default:
                            if (selectedPeriod === 'skirmish') {
                                return (b.monthlyXP || 0) - (a.monthlyXP || 0);
                            }
                            return (b.level || 1) - (a.level || 1) || (b.xp || 0) - (a.xp || 0) || b.totalBadges - a.totalBadges;
                    }
                });
                
                setLeaderboard(sortedData);
                
                // Extract spotlight users
                const spotlightUsers = sortedData.filter(user => 
                    user.spotlight && Object.values(user.spotlight).some(Boolean)
                );
                setSpotlightUsers(spotlightUsers);
                
                setLoading(false);
            } catch (error) {
                console.error('Error fetching leaderboard:', error);
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, [selectedCategory, selectedPeriod]);

    const handleCategoryChange = (event, newValue) => {
        setSelectedCategory(newValue);
        setPage(0); // Reset to first page when changing category
    };

    const handleShowProfile = (user) => {
        setSelectedUser(user);
        setProfileOpen(true);
    };

    const handleCloseProfile = () => {
        setProfileOpen(false);
    };

    const handleShareSuccess = () => {
        setShareAlert(true);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleFilterChange = (name, value) => {
        setFilterOptions(prev => ({
            ...prev,
            [name]: value
        }));
        setPage(0);
    };

    // Filter the leaderboard data based on search and filters
    const filteredLeaderboard = leaderboard
        .filter(user => {
            const matchesSearch = searchTerm === '' || 
                user.displayName.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesWPM = user.readingStats.averageWPM >= filterOptions.minWPM && 
                user.readingStats.averageWPM <= filterOptions.maxWPM;
            
            // Add friend filtering logic here when friend system is implemented
            const matchesFriends = !filterOptions.showFriends || true; // Placeholder until friends feature is implemented
            
            return matchesSearch && matchesWPM && matchesFriends;
        });
    
    // No need for additional sorting here as the data is already sorted in the useEffect
    const paginatedLeaderboard = filteredLeaderboard
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const currentMonthName = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Reader Showcase
            </Typography>
            <Typography color="text.secondary" paragraph>
                Celebrate reading achievements and see how you stack up against other readers.
            </Typography>

            {/* Period Toggle: Hall of Legends vs Monthly Skirmish */}
            <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Tabs value={selectedPeriod} onChange={(e, v) => { setSelectedPeriod(v); setPage(0); }}
                    variant="fullWidth"
                    sx={{ 
                        '& .MuiTab-root': { py: 2 },
                        '& .Mui-selected': { fontWeight: 'bold' }
                    }}>
                    <Tab value="legends" label="Hall of Legends" icon={<TrophyIcon sx={{ color: '#FFD700' }} />} iconPosition="start" />
                    <Tab value="skirmish" label={`Monthly Skirmish`} icon={<FireIcon sx={{ color: '#FF5722' }} />} iconPosition="start" />
                </Tabs>
                <Box sx={{ p: 2, backgroundColor: selectedPeriod === 'skirmish' ? 'rgba(255,87,34,0.05)' : 'rgba(255,215,0,0.05)' }}>
                    {selectedPeriod === 'legends' ? (
                        <Typography variant="body2" color="text.secondary">
                            All-time rankings. This board never resets — your legacy lives forever.
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            <strong>{currentMonthName}</strong> — Compete for this month's top spot. Resets on the 1st.
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* Reader Spotlight Section (only on Legends) */}
            {selectedPeriod === 'legends' && (
                <ReaderSpotlight 
                    spotlightUsers={spotlightUsers} 
                    onShowProfile={handleShowProfile} 
                />
            )}

            {/* Leaderboard Section */}
            <Paper sx={{ mt: 4, p: 3, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrophyIcon sx={{ color: '#FFD700', mr: 1 }} /> Leaderboard
                </Typography>
                <Typography color="text.secondary" paragraph>
                    See how you stack up against other readers in various achievement categories.
                </Typography>

                {/* Search and Filter Bar */}
                <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        label="Search Users"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{ flexGrow: 1, minWidth: '200px' }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button 
                            variant={filterOptions.showFriends ? "contained" : "outlined"}
                            size="small"
                            onClick={() => handleFilterChange('showFriends', !filterOptions.showFriends)}
                            startIcon={<PersonIcon />}
                        >
                            Friends Only
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const min = prompt('Enter minimum WPM:', filterOptions.minWPM);
                                if (min !== null) {
                                    const numMin = parseInt(min, 10);
                                    if (!isNaN(numMin)) {
                                        handleFilterChange('minWPM', numMin);
                                    }
                                }
                            }}
                        >
                            Min WPM: {filterOptions.minWPM}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const max = prompt('Enter maximum WPM:', filterOptions.maxWPM);
                                if (max !== null) {
                                    const numMax = parseInt(max, 10);
                                    if (!isNaN(numMax)) {
                                        handleFilterChange('maxWPM', numMax);
                                    }
                                }
                            }}
                        >
                            Max WPM: {filterOptions.maxWPM === 10000 ? '∞' : filterOptions.maxWPM}
                        </Button>
                    </Box>
                </Box>

                {/* Category Tabs */}
                <Tabs
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ 
                        mb: 3,
                        '.MuiTab-root': {
                            minWidth: 'unset'
                        }
                    }}
                >
                    {Object.entries(BADGE_CATEGORIES).map(([category, data]) => {
                        const Icon = data.icon;
                        return (
                            <Tab
                                key={category}
                                value={category}
                                icon={<Icon sx={{ color: data.color }} />}
                                label={data.title}
                                sx={{
                                    '&.Mui-selected': {
                                        color: data.color
                                    }
                                }}
                            />
                        );
                    })}
                </Tabs>
                
                {/* Display results count */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Showing {filteredLeaderboard.length} {filteredLeaderboard.length === 1 ? 'reader' : 'readers'}
                    {searchTerm && ` matching "${searchTerm}"`}
                    {filterOptions.minWPM > 0 && ` with WPM ≥ ${filterOptions.minWPM}`}
                    {filterOptions.maxWPM < 10000 && ` with WPM ≤ ${filterOptions.maxWPM}`}
                </Typography>

                {/* Leaderboard Cards */}
                {paginatedLeaderboard.length > 0 ? (
                    paginatedLeaderboard.map((user, index) => (
                        <LeaderboardCard
                            key={user._id}
                            user={user}
                            rank={page * rowsPerPage + index}
                            category={selectedCategory}
                            period={selectedPeriod}
                            onShowProfile={handleShowProfile}
                        />
                    ))
                ) : (
                    <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <Typography variant="body1">
                            No readers found matching your criteria.
                        </Typography>
                    </Paper>
                )}

                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <TablePagination
                        component="div"
                        count={filteredLeaderboard.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </Box>
            </Paper>

            {/* Friend Challenge Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <InsightsIcon sx={{ mr: 1, color: '#2196F3' }} />
                    Reading Challenges
                </Typography>
                <Typography paragraph>
                    Challenge yourself or friends to reach specific reading goals! Choose a metric and set a target.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Speed Challenge
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Reach 1,500 WPM with at least 80% comprehension
                                </Typography>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    sx={{ mt: 2 }}
                                    onClick={() => alert('Challenge feature coming soon!')}
                                >
                                    Accept Challenge
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Streak Challenge
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Maintain a 7-day reading streak
                                </Typography>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    sx={{ mt: 2 }}
                                    onClick={() => alert('Challenge feature coming soon!')}
                                >
                                    Accept Challenge
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Volume Challenge
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Read 10,000 words in one week
                                </Typography>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    sx={{ mt: 2 }}
                                    onClick={() => alert('Challenge feature coming soon!')}
                                >
                                    Accept Challenge
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Paper>

            {/* Reader Profile Dialog */}
            <Dialog 
                open={profileOpen} 
                onClose={handleCloseProfile}
                maxWidth="sm"
                fullWidth
            >
                {selectedUser && (
                    <ReaderCard 
                        user={selectedUser} 
                        onClose={handleCloseProfile}
                        onShare={handleShareSuccess}
                    />
                )}
            </Dialog>

            {/* Share Success Alert */}
            <Snackbar 
                open={shareAlert} 
                autoHideDuration={6000} 
                onClose={() => setShareAlert(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setShareAlert(false)} severity="success" sx={{ width: '100%' }}>
                    Reader card created successfully! Share your reading achievements with friends.
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Leaderboard;
